import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderTree,
  FileText,
  Inbox,
  Bell,
  ShieldCheck,
  Users,
  Settings,
  LogOut,
  PenSquare,
  Sparkles,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  FileUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABEL = {
  admin: "Admin",
  champion: "Process Champion",
  owner: "Process Owner",
  user: "Reader",
};

function Item({ to, icon: Icon, label, testId, collapsed }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      title={label}
      className={({ isActive }) =>
        [
          "flex items-center rounded-2xl text-sm transition-all duration-200 min-w-0",
          collapsed
            ? "h-10 w-10 mx-auto justify-center p-0 gap-0 shrink-0"
            : "gap-3 px-3 py-2.5 w-full",
          isActive
            ? "text-white bg-gradient-to-r from-[#2563EB]/40 via-[#4F46E5]/30 to-[#06B6D4]/20 border border-white/10 shadow-[0_0_20px_-5px_rgba(79,70,229,0.55)]"
            : "text-[#9AA4B2] hover:text-white hover:bg-white/[0.04] border border-transparent",
        ].join(" ")
      }
    >
      <Icon size={18} strokeWidth={1.8} className="shrink-0" aria-hidden />
      {!collapsed && <span className="font-medium truncate">{label}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(
    localStorage.getItem("sidebar_collapsed") === "true"
  );

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("sidebar-toggle", { detail: collapsed }));
  }, [collapsed]);

  if (!user) return null;
  const role = user.role;

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar_collapsed", next ? "true" : "false");
    window.dispatchEvent(new CustomEvent("sidebar-toggle", { detail: next }));
  };

  const initials = (user.name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2);

  return (
    <aside
      className={[
        "hidden lg:flex fixed left-4 top-4 bottom-4 flex-col glass rounded-3xl z-30",
        "transition-[width,padding] duration-300",
        collapsed ? "w-[4.5rem] p-2" : "w-64 p-3",
      ].join(" ")}
      data-testid="sidebar"
    >
      {/* Brand */}
      <div
        className={[
          "flex items-center shrink-0",
          collapsed ? "justify-center pt-2 pb-3" : "gap-2.5 px-1 pt-1 pb-4",
        ].join(" ")}
      >
        <div className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-[#2563EB] via-[#4F46E5] to-[#06B6D4] flex items-center justify-center shadow-[0_0_20px_-4px_rgba(79,70,229,0.7)]">
          <Sparkles size={18} className="text-white" strokeWidth={2.2} />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-display font-bold text-lg tracking-tight text-white truncate">
              SOPFlow
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6472] truncate">
              Ops Excellence
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={toggleCollapse}
        className="absolute -right-3.5 top-8 z-40 w-7 h-7 rounded-full border border-white/20 bg-[#0A0E1A] shadow-[0_0_16px_-2px_rgba(79,70,229,0.55)] flex items-center justify-center text-[#C7D2FE] hover:text-white hover:bg-[#121826] hover:border-indigo-400/50 transition"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Nav scrolls when zoomed / many items; footer stays put */}
      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-1 py-1">
        <Item to="/dashboard" icon={LayoutDashboard} label="Command Center" testId="nav-dashboard" collapsed={collapsed} />
        <Item to="/library" icon={FileText} label="SOP Library" testId="nav-library" collapsed={collapsed} />
        <Item to="/chat" icon={MessageSquare} label="Ask AI" testId="nav-chat" collapsed={collapsed} />
        {(role === "champion" || role === "admin") && (
          <>
            <Item to="/create" icon={PenSquare} label="Create SOP" testId="nav-create" collapsed={collapsed} />
            <Item to="/import/pdf" icon={FileUp} label="PDF Import" testId="nav-pdf-import" collapsed={collapsed} />
          </>
        )}
        {(role === "owner" || role === "admin") && (
          <Item to="/approvals" icon={Inbox} label="Approvals" testId="nav-approvals" collapsed={collapsed} />
        )}
        <Item to="/notifications" icon={Bell} label="Notifications" testId="nav-notifications" collapsed={collapsed} />
        <Item to="/compliance" icon={ShieldCheck} label="Compliance" testId="nav-compliance" collapsed={collapsed} />
        {(role === "owner" || role === "admin") && (
          <Item to="/ownership" icon={FolderTree} label="Ownership" testId="nav-ownership" collapsed={collapsed} />
        )}
        <Item to="/profile" icon={Settings} label="Settings" testId="nav-profile" collapsed={collapsed} />
        {role === "admin" && (
          <Item to="/admin" icon={Users} label="Admin" testId="nav-admin" collapsed={collapsed} />
        )}
      </nav>

      {/* Account footer — never compress under zoom */}
      <div className="shrink-0 mt-2 pt-3 border-t border-white/10 space-y-1">
        <button
          type="button"
          onClick={() => navigate("/profile")}
          data-testid="sidebar-profile-btn"
          title={user.name || "My Settings"}
          className={[
            "w-full flex items-center rounded-2xl hover:bg-white/[0.04] transition min-w-0",
            collapsed ? "h-10 w-10 mx-auto justify-center p-0" : "gap-2.5 px-2 py-2",
          ].join(" ")}
        >
          <div className="w-9 h-9 shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500/60 to-cyan-500/60 flex items-center justify-center border border-white/10">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-xs font-semibold leading-none">{initials}</span>
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 text-left min-w-0 overflow-hidden">
              <div className="text-sm text-white truncate">{user.name}</div>
              <div className="text-[10px] text-[#9AA4B2] truncate">{ROLE_LABEL[role]}</div>
            </div>
          )}
        </button>

        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/signin");
          }}
          data-testid="sidebar-logout-btn"
          title="Sign out"
          className={[
            "w-full flex items-center rounded-2xl text-[#9AA4B2] hover:text-white hover:bg-white/[0.04] text-sm transition",
            collapsed ? "h-10 w-10 mx-auto justify-center p-0" : "gap-2 px-3 py-2",
          ].join(" ")}
        >
          <LogOut size={15} className="shrink-0" />
          {!collapsed && <span className="truncate">Sign out</span>}
        </button>
      </div>
    </aside>
  );
}
